import React, { useState } from "react";
import classes from "./QuickReels.module.scss";
import CustomImage from "../../CustomImage";
import useIsMobile from "../../../hooks/useIsMobile";
import useEmblaOverflow from "../../../hooks/useEmblaOverflow";
import LazyMedia from "../../LazyMedia";
import { convertYouTubeToEmbed, getYouTubeVideoId } from "../../../utils/helpers";

const ReelCard = ({ reel }: { reel: any }) => {
  const [isPlaying, setIsPlaying] = useState(false);

  const handlePlay = () => {
    if (reel.link) setIsPlaying(true);
  };

  if (isPlaying) {
    const embedBase = convertYouTubeToEmbed(reel.link) ?? reel.link;
    const sep = embedBase.includes("?") ? "&" : "?";
    const embedSrc = `${embedBase}${sep}autoplay=1`;
    return (
      <div className={classes["reel-card"]}>
        <LazyMedia
          iframeClassName={classes["tm-video"]}
          src={embedSrc}
          title="YouTube video player"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        />
      </div>
    );
  }

  const videoId = getYouTubeVideoId(reel.link);
  const youtubeThumbSrc = videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null;
  const customThumbnail = reel?.thumbnail?.url;

  return (
    <div className={classes["reel-card"]} onClick={handlePlay}>
      {youtubeThumbSrc || customThumbnail ? (
        <CustomImage
          src={customThumbnail || youtubeThumbSrc}
          alt={reel.title || "Video thumbnail"}
          layout="fill"
          objectFit={customThumbnail ? "fill" : "cover"}
        />
      ) : (
        <div className={classes["reel-placeholder"]} />
      )}
      <div className={classes["play-overlay"]}>
        <div className={classes["play-button-wrapper"]}>
          <div className={classes["play-button"]}>
            <svg width="16" height="18" viewBox="0 0 16 18" fill="none">
              <path d="M15 7.268a2 2 0 010 3.464L3 17.856A2 2 0 010 16.124V1.876A2 2 0 013 .144L15 7.268z" fill="#333" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

const QuickReels = ({ countryData }: { countryData: any }) => {
  const reelsData = countryData?.quickReels ?? [];
  const isMobile = useIsMobile(768);
  const { emblaRef, hasOverflow, canScrollPrev, canScrollNext, scrollPrev, scrollNext } = useEmblaOverflow();

  const renderCards = () =>
    reelsData?.reels?.map((reel: any) => (
      <div className={classes.slide} key={reel.id}>
        <ReelCard reel={reel ?? ""} />
      </div>
    ));

  const arrowLeft = (
    <button
      className={`${classes["arrow-btn"]}${canScrollPrev ? "" : ` ${classes["arrow-btn-disabled"]}`}`}
      onClick={scrollPrev}
      disabled={!canScrollPrev}
      aria-label="Previous"
    >
      <CustomImage src="/new-images/left-arrow.svg" height={17} width={10} alt="left-arrow" />
    </button>
  );

  const arrowRight = (
    <button
      className={`${classes["arrow-btn"]}${canScrollNext ? "" : ` ${classes["arrow-btn-disabled"]}`}`}
      onClick={scrollNext}
      disabled={!canScrollNext}
      aria-label="Next"
    >
      <CustomImage src="/new-images/right-arrow.svg" height={17} width={10} alt="right-arrow" />
    </button>
  );

  return (
    <section id="quick-reels" className={classes["quickreels-section"]}>
      <div className={classes["quickreels-container"]}>
        <h2 className={classes["heading"]}>{reelsData?.title ?? ""}</h2>
        <div className={classes["subtext"]} dangerouslySetInnerHTML={{ __html: reelsData?.description ?? "" }}></div>

        {isMobile ? (
          <div className={classes["carousel-wrapper"]}>
            <div className={classes.viewport} ref={emblaRef}>
              <div className={classes["slider-container"]}>{renderCards()}</div>
            </div>
            {hasOverflow && (
              <div className={classes["arrows-container"]}>
                {arrowLeft}
                {arrowRight}
              </div>
            )}
          </div>
        ) : (
          <>
            <div style={reelsData?.reels?.length < 5 ? { justifyContent: "center" } : {}} className={classes["carousel-wrapper"]}>
              <div className={classes.viewport} ref={emblaRef}>
                <div style={reelsData?.reels?.length < 5 ? { justifyContent: "center" } : {}} className={classes["slider-container"]}>
                  {renderCards()}
                </div>
              </div>
            </div>
            {hasOverflow && (
              <div className={classes["arrows-container"]}>
                {arrowLeft}
                {arrowRight}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default QuickReels;
